/**
 * The engine's own rules for levelling, perks, skill points, radiation and
 * poison — the ones the editor has to obey when it is in engine mode.
 *
 * Everything here is transcribed from `engine/src`, with the function named at
 * each rule. Nothing is inferred from how the game "feels", and nothing is taken
 * from a wiki. Where the engine has a knob the save file does not record, the
 * default is used and the fact is stated rather than hidden — see
 * `UNESTABLISHED` at the bottom, which the UI shows verbatim.
 *
 * This module is pure: no save, no DOM. `edits.ts` applies it, `ui/saveedit.ts`
 * displays it, and `scripts/e2e-saveedit.mjs` checks it.
 */

import {
  PC_LEVEL_MAX,
  PRIMARY_STAT_COUNT,
  SKILL_COUNT,
  perkIndex,
  statIndex,
  traitIndex,
} from './constants';
import { PERK_PARAM_GVAR_BIT, PERK_PARAM_MODE_AND, PERK_PARAM_MODE_FIRST_ONLY, PERK_ROWS, type PerkRow } from './perktable';
import { forGame } from './constants-fo1';
import type { SaveGameType } from './gamedefs';

/**
 * Every rule below takes the game as its last argument and defaults to Fallout
 * 2, so the Fallout 2 call sites read exactly as they did. Where a rule is the
 * same in both engines that is stated; where it differs the two are set side by
 * side with both citations, because "Fallout 1 is Fallout 2 with smaller
 * numbers" is wrong often enough to be dangerous — the skill point curve is flat
 * rather than banded, the skill cap is 200 rather than 300, and the perk grant
 * assigns rather than accumulates.
 */
type Game = SaveGameType;

// ---------------------------------------------------------------- levelling

/**
 * XP needed to *be* level `level` (engine/src/stat.cc `pcGetExperienceForLevel`).
 *
 * The engine writes it as two integer-division branches:
 *
 *     halfLevel = level / 2
 *     odd  -> 1000 * halfLevel * level
 *     even -> 1000 * halfLevel * (level - 1)
 *
 * Both collapse to `500 * level * (level - 1)`, which is what is used here
 * because it is obviously monotonic and obviously exact in doubles at these
 * magnitudes (level 99 is 4,851,000 — nowhere near 2^53).
 *
 * Level 1 is 0 XP, so this is the *floor* of a level's XP band; the ceiling is
 * one less than the next level's floor.
 */
export function experienceForLevel(level: number, game: Game = 'fo2'): number {
  const cap = levelCap(game);
  if (level < 1 || level > cap) return -1;
  return 500 * level * (level - 1);
}

/**
 * `PC_LEVEL_MAX` — 99 in Fallout 2 (engine/src/stat_defs.h), 21 in Fallout 1
 * (fallout1-ce stat_defs.h:18).
 *
 * The *curve* below the cap is the same in both, which is worth stating because
 * it does not look that way in the source. Fallout 1's `stat_pc_min_exp`
 * (stat.cc:495) is a literal table of `1000 * {0, 1, 3, 6, 10, 15, ...}` — the
 * triangular numbers — and `1000 * (L-1)L/2` is `500 * L * (L-1)`, which is what
 * Fallout 2's `pcGetExperienceForLevel` computes directly. Same thresholds, one
 * written out and one derived.
 */
export function levelCap(game: Game = 'fo2'): number {
  return game === 'fo1' ? forGame('fo1').pcLevelMax : PC_LEVEL_MAX;
}

/** The XP band `[floor, ceiling]` a level occupies. Top level has no ceiling. */
export function experienceBand(level: number, game: Game = 'fo2'): { floor: number; ceiling: number | null } {
  const floor = experienceForLevel(level, game);
  const next = experienceForLevel(level + 1, game);
  return { floor, ceiling: next < 0 ? null : next - 1 };
}

/** The level a given XP total buys (`pcGetLevelForExperience`, same table). */
export function levelForExperience(experience: number, game: Game = 'fo2'): number {
  const cap = levelCap(game);
  let level = 1;
  while (level < cap && experienceForLevel(level + 1, game) <= experience) level++;
  return level;
}

/**
 * Skill points granted by *one* level-up
 * (engine/src/character_editor.cc `characterEditorUpdateLevel`, the loop body):
 *
 *     sp += 5 + skillPointsPerLevelModifier
 *     sp += critterGetBaseStatWithTraitModifier(gDude, STAT_INTELLIGENCE) * 2
 *     sp += perkGetRank(gDude, PERK_EDUCATED) * 2
 *     sp += traitIsSelected(TRAIT_SKILLED) * 5
 *     if (traitIsSelected(TRAIT_GIFTED)) sp -= 5
 *
 * Two details that are easy to get wrong and both matter here:
 *
 *   - Intelligence is `critterGetBaseStatWithTraitModifier`, so **base plus
 *     traits and nothing else**. The stored bonus and the GAIN_INTELLIGENCE perk
 *     do not count, even though the character screen shows them.
 *   - `skillPointsPerLevelModifier` is a fork knob (character_editor.cc:834),
 *     settable only from script. A save file does not record it, so the editor
 *     uses the engine's own reset value of 0 and says so.
 */
export function skillPointsForOneLevel(opts: {
  /** base + trait Intelligence, *not* the character-screen value. */
  intelligence: number;
  educatedRank: number;
  skilled: boolean;
  gifted: boolean;
}, game: Game = 'fo2'): number {
  if (game === 'fo1') {
    // fallout1-ce `UpdateLevel` (editor.cc:5203-5220). Three differences, and
    // none of them is cosmetic:
    //   - Skilled grants no skill points here at all. In Fallout 1 it is worth
    //     +10% to every skill (`trait_adjust_skill`) and an extra level between
    //     perks, not five points a level.
    //   - Gifted's -5 is clamped to zero *at this step*, before the pool cap.
    //   - Intelligence is `stat_get_base`, which for the player adds
    //     `trait_adjust_stat` (stat.cc) — the same base-plus-trait value
    //     Fallout 2 reads via `critterGetBaseStatWithTraitModifier`. So
    //     Gifted's +1 INT does feed back into the points it also subtracts from.
    const sp = 5 + opts.intelligence * 2 + opts.educatedRank * 2 - (opts.gifted ? 5 : 0);
    return Math.max(sp, 0);
  }
  return 5 + opts.intelligence * 2 + opts.educatedRank * 2 + (opts.skilled ? 5 : 0) - (opts.gifted ? 5 : 0);
}

/**
 * The engine clamps the *accumulated unspent pool*, not the increment
 * (`sp = std::clamp(sp, 0, 99)` inside the same loop). Banking more than 99
 * unspent points is therefore impossible through a level-up, which is why the
 * editor accrues level by level rather than multiplying.
 */
export const UNSPENT_SKILL_POINT_CAP = 99;

/**
 * Walk `from` -> `to` one level at a time, accruing skill points the way the
 * engine does — clamping the pool at each step, not at the end.
 *
 * Returns the new unspent total. Going down levels does not refund: the engine
 * has no path that takes a level away, so there is nothing to be faithful to.
 * Expert mode is where that is edited.
 */
export function accrueSkillPoints(
  unspent: number,
  fromLevel: number,
  toLevel: number,
  perLevel: Parameters<typeof skillPointsForOneLevel>[0],
  game: Game = 'fo2',
): number {
  let pool = unspent;
  const gain = skillPointsForOneLevel(perLevel, game);
  for (let level = fromLevel + 1; level <= toLevel; level++) {
    pool = Math.min(Math.max(pool + gain, 0), UNSPENT_SKILL_POINT_CAP);
  }
  return pool;
}

/**
 * How many levels between perk slots
 * (engine/src/character_editor.cc `characterEditorGetLevelsPerPerk`): three,
 * four with Skilled.
 */
export function levelsPerPerk(skilled: boolean): number {
  return skilled ? 4 : 3;
}

/**
 * How many perk slots a character has been granted by reaching `level`
 * (`characterEditorGrantPerkAtLevel`: the test is `level % N == 0`, on the level
 * *number*).
 *
 * The consequence worth surfacing, because it surprises people: the cadence is
 * on the number, not on how many level-ups happened. Adding Skilled at level 10
 * does not take a perk away, but it re-phases the schedule — the next slot moves
 * from 12 to 12 (4×3), and from then on they land on multiples of four.
 */
export function perkSlotsThroughLevel(level: number, skilled: boolean): number {
  return Math.floor(level / levelsPerPerk(skilled));
}

/**
 * engine/src/character_editor.cc `kMaxSelectablePerks` — the perk dialog stops
 * offering anything once this many *distinct* perks are held. Fallout 1's
 * `UpdateLevel` hard-codes the same test as `selectedPerksCount < 7`
 * (fallout1-ce editor.cc:5222).
 */
export const MAX_SELECTABLE_PERKS = 37;
export function maxSelectablePerks(game: Game = 'fo2'): number {
  return game === 'fo1' ? 7 : MAX_SELECTABLE_PERKS;
}

/**
 * engine/src/character_editor.cc `kMaxPerkOwed` — the ceiling the owed-perk
 * counter saturates at, and what `characterEditorSetPerkOwed` refuses past.
 *
 * Fallout 1 has no backlog to cap: `UpdateLevel` does `free_perk = 1`, an
 * assignment (editor.cc:5228). Crossing three perk levels with the character
 * screen closed owes one perk in Fallout 1 and three in Fallout 2. So the
 * "ceiling" for Fallout 1 is 1, and it is a real behavioural difference rather
 * than a smaller version of the same rule.
 */
export const MAX_PERKS_OWED = 250;
export function maxPerksOwed(game: Game = 'fo2'): number {
  return game === 'fo1' ? 1 : MAX_PERKS_OWED;
}

/** True when crossing perk levels *sets* the owed count rather than adding to it. */
export function perkGrantAssigns(game: Game = 'fo2'): boolean {
  return game === 'fo1';
}

// -------------------------------------------------------------- skill points

/**
 * Skill points to raise a skill by one percent, from its *current displayed*
 * percentage (engine/src/skill.cc `skillsGetCost`).
 *
 * This is the answer to "why did my 400 points only buy 60%": past 100% each
 * point of skill costs more than one skill point, and past 200% it costs six.
 */
export function skillPointCost(displayedValue: number, game: Game = 'fo2'): number {
  // Fallout 1 has no cost curve at all: `skill_inc_point` (skill.cc:268) spends
  // exactly one point per raw point, right up to `SKILL_LEVEL_MAX`. The bands
  // below are a Fallout 2 invention.
  if (game === 'fo1') return 1;
  if (displayedValue >= 201) return 6;
  if (displayedValue >= 176) return 5;
  if (displayedValue >= 151) return 4;
  if (displayedValue >= 126) return 3;
  if (displayedValue >= 101) return 2;
  return 1;
}

/** The cost bands, for the explanatory panel. `[from, to, cost]`, to inclusive. */
export const SKILL_COST_BANDS: ReadonlyArray<readonly [number, number | null, number]> = [
  [0, 100, 1],
  [101, 125, 2],
  [126, 150, 3],
  [151, 175, 4],
  [176, 200, 5],
  [201, null, 6],
];

/**
 * engine/src/skill.cc `skillMaximum`. Applied at the *end* of `skillGetValue`,
 * after the tag doubling, the +20 tag bonus and every trait/perk modifier — so
 * the honest thing to show is both numbers: the sum the formula produced, and
 * the 300 the engine will hand the character screen.
 */
export const SKILL_DISPLAY_CAP = 300;

/** …and Fallout 1's `SKILL_LEVEL_MAX` (fallout1-ce skill.cc:31) is 200. */
export function skillDisplayCap(game: Game = 'fo2'): number {
  return game === 'fo1' ? forGame('fo1').skillMaximum : SKILL_DISPLAY_CAP;
}

/**
 * `skillAdd` (skill.cc:569) refuses when `skillGetValue >= skillMaximum`, so at
 * the cap further points buy nothing at all. That is the fact behind the owner's
 * "255 and 280 both say 300%". Fallout 1's `skill_inc_point` refuses on the same
 * test against its own 200.
 */
export function skillIsCapped(uncappedValue: number, game: Game = 'fo2'): boolean {
  return uncappedValue > skillDisplayCap(game);
}

// -------------------------------------------------------------------- perks

export type PerkRequirement =
  | { kind: 'level'; needed: number; have: number; met: boolean }
  | { kind: 'stat'; stat: number; needed: number; below: boolean; have: number; met: boolean }
  | { kind: 'skill'; skill: number; needed: number; below: boolean; have: number; met: boolean }
  | { kind: 'gvar'; gvar: number; needed: number; below: boolean; have: number; met: boolean }
  | { kind: 'rank'; needed: number; have: number; met: boolean }
  | { kind: 'not-selectable' }
  | { kind: 'perk-limit'; needed: number; have: number; met: boolean };

export type PerkAvailability = {
  perk: number;
  /** True when `perkCanAddAtLevel` would return true. */
  available: boolean;
  /** Every requirement the engine tests, met or not, in the order it tests them. */
  requirements: PerkRequirement[];
  maxRank: number;
  currentRank: number;
};

/** What `perkCanAddAtLevel` needs to know about the character. */
export type PerkContext = {
  level: number;
  /** `critterGetStat` for the SPECIAL seven — the character-screen values. */
  special: (stat: number) => number;
  /** `skillGetValue` for a skill index. */
  skill: (skill: number) => number;
  /** `gameGetGlobalVar`. */
  gvar: (index: number) => number;
  /** Current rank of a perk. */
  rank: (perk: number) => number;
  /**
   * The perk table to test against. Omitted means Fallout 2's; a Fallout 1
   * caller passes `forGame('fo1').perkRows`, which is the same seventeen columns
   * read out of fallout1-ce's own narrower struct (see `perktable-fo1.ts`).
   */
  perkRows?: readonly PerkRow[];
  /**
   * Which engine's *comparison* rules to use. Omitted means Fallout 2's.
   *
   * This is not the same knob as `perkRows`, and conflating them was a bug:
   * Fallout 2's `perkCanAddAtLevel` reads a negative stat or skill requirement
   * as "must be strictly below its absolute value", while fallout1-ce's
   * `perk_can_add` (perk.cc:187) has no such branch at all — it is a flat
   * `stat_level(obj_dude, stat) < required_stat_levels[stat]` and a flat
   * `skill_level(...) < required_skill_level`, so a negative threshold there is
   * simply always satisfied. Stock Fallout 1 only puts negatives on rows whose
   * `max_rank` is -1 (the addiction perks), which return early, so this changes
   * nothing on stock data — but the editor was applying Fallout 2's inverted
   * meaning to a Fallout 1 table, and a modded table would have hit it.
   */
  game?: Game;
};

/**
 * `perkCanAddAtLevel` (engine/src/perk.cc:302), faithfully — including the two
 * asymmetries that a reimplementation gets wrong if it tidies them up:
 *
 *   - A **negative** SPECIAL requirement means the stat must be *strictly below*
 *     its absolute value (`if (critterGetStat(...) >= -stats[stat]) return false`).
 *     Only one thing in stock data uses it, and getting it backwards would
 *     silently invert a whole perk's availability.
 *   - A **negative** `value1`/`value2` means the same for a skill (compared
 *     against `-value1`) but *not* for a GVAR, where the raw negative value is
 *     the threshold. That is not a tidy rule; it is what the engine does, so it
 *     is what this does.
 *
 * `paramMode` is faithful too: mode 0 tests param1 only, mode 1 (OR) tests
 * param2 only when param1 failed, mode 2 (AND) requires both.
 */
export function perkAvailability(perk: number, ctx: PerkContext): PerkAvailability {
  const rows = ctx.perkRows ?? PERK_ROWS;
  // fallout1-ce's `perk_can_add` compares with a plain `<` and never inverts on
  // a negative threshold. See `PerkContext.game`.
  const negativeMeansBelow = (ctx.game ?? 'fo2') !== 'fo1';
  const row = rows[perk];
  const currentRank = ctx.rank(perk);
  if (!row) {
    return { perk, available: false, requirements: [{ kind: 'not-selectable' }], maxRank: 0, currentRank };
  }
  const [, maxRank, minLevel, , , param1Raw, value1, paramMode, param2Raw, value2] = row;
  const requirements: PerkRequirement[] = [];

  // `maxRank == -1` is the engine's "not offered in the perk dialog" marker:
  // addictions, armour bonuses, the GAIN_* perks. Things scripts and items give.
  if (maxRank === -1) {
    return { perk, available: false, requirements: [{ kind: 'not-selectable' }], maxRank, currentRank };
  }

  requirements.push({ kind: 'rank', needed: maxRank, have: currentRank, met: currentRank < maxRank });
  requirements.push({ kind: 'level', needed: minLevel, have: ctx.level, met: ctx.level >= minLevel });

  /** One param1/param2 test, which is a skill test or a gvar test. */
  type ParamRequirement = Extract<PerkRequirement, { kind: 'skill' | 'gvar' }>;
  const paramRequirement = (raw: number, needed: number): ParamRequirement | null => {
    if (raw === -1) return null;
    const isGvar = (raw & PERK_PARAM_GVAR_BIT) !== 0;
    const index = raw & ~PERK_PARAM_GVAR_BIT;
    const below = negativeMeansBelow && needed < 0;
    if (isGvar) {
      const have = ctx.gvar(index);
      // The engine compares against the *raw* negative for gvars, not its
      // absolute value. Faithful, not tidy.
      return { kind: 'gvar', gvar: index, needed, below, have, met: below ? have < needed : have >= needed };
    }
    const have = ctx.skill(index);
    return {
      kind: 'skill',
      skill: index,
      needed: below ? -needed : needed,
      below,
      have,
      met: below ? have < -needed : have >= needed,
    };
  };

  const first = paramRequirement(param1Raw, value1);
  const second = paramRequirement(param2Raw, value2);
  if (first) requirements.push(first);
  // param2 is only *required* under AND; under OR it is an alternative and is
  // shown as one, and under FIRST_ONLY it is not consulted at all.
  if (second && paramMode !== PERK_PARAM_MODE_FIRST_ONLY) requirements.push(second);

  // Kept out of the `every` below, because under OR a failed param1 is not a
  // failure — it is the reason param2 gets looked at.
  let paramsMet: boolean;
  if (!first) paramsMet = true;
  else if (paramMode === PERK_PARAM_MODE_FIRST_ONLY) paramsMet = first.met;
  else if (paramMode === PERK_PARAM_MODE_AND) paramsMet = first.met && (second?.met ?? false);
  else paramsMet = first.met || (second?.met ?? false);

  const paramRows: PerkRequirement[] = [first, second].filter((r): r is ParamRequirement => r !== null);

  for (let stat = 0; stat < PRIMARY_STAT_COUNT; stat++) {
    const needed = row[10 + stat] as number;
    if (needed === 0) continue; // a no-op: every stat is >= 0
    const below = negativeMeansBelow && needed < 0;
    const have = ctx.special(stat);
    requirements.push({
      kind: 'stat',
      stat,
      needed: below ? -needed : needed,
      below,
      have,
      met: below ? have < -needed : have >= needed,
    });
  }

  const available =
    requirements.every((r) => paramRows.includes(r) || (r.kind !== 'not-selectable' && r.met)) && paramsMet;

  return { perk, available, requirements, maxRank, currentRank };
}

/** Every perk the engine would offer, in perk order. */
export function availablePerks(ctx: PerkContext): PerkAvailability[] {
  const rows = ctx.perkRows ?? PERK_ROWS;
  const out: PerkAvailability[] = [];
  for (let perk = 0; perk < rows.length; perk++) {
    const availability = perkAvailability(perk, ctx);
    if (availability.available) out.push(availability);
  }
  return out;
}

/** Is this perk one the perk dialog would ever list? (`maxRank != -1`.) */
export function perkIsSelectable(perk: number, rows: readonly PerkRow[] = PERK_ROWS): boolean {
  return (rows[perk]?.[1] ?? -1) !== -1;
}

/** The perk's declared maximum rank, or -1 for the script-only perks. */
export function perkMaxRank(perk: number, rows: readonly PerkRow[] = PERK_ROWS): number {
  return rows[perk]?.[1] ?? -1;
}

// ---------------------------------------------------------------- radiation

/**
 * engine/src/critter.cc `critterCheckRadiationEvent` — the rad thresholds, and
 * `gRadiationEffectPenalties` — what each level costs.
 *
 * The indexing is the engine's and it is off by one on purpose:
 * `radiationProcess` applies row `radiationLevel - 1`, so the level named here
 * is paired with the penalties it actually causes. MINOR really does cost
 * nothing but a message.
 */
export const RADIATION_LEVELS: ReadonlyArray<{
  name: string;
  /** Lowest rad count that reaches this level. */
  from: number;
  /** Highest, or null for the open top band. */
  to: number | null;
  /** ST, PE, EN, CH, IN, AG, then current HP and healing rate. */
  penalties: { readonly stat: string; readonly amount: number }[];
}> = [
  { name: 'None', from: 0, to: 99, penalties: [] },
  { name: 'Minor', from: 100, to: 199, penalties: [] },
  { name: 'Advanced', from: 200, to: 399, penalties: [{ stat: 'STRENGTH', amount: -1 }] },
  {
    name: 'Critical',
    from: 400,
    to: 599,
    penalties: [
      { stat: 'STRENGTH', amount: -1 },
      { stat: 'AGILITY', amount: -1 },
      { stat: 'HEALING_RATE', amount: -3 },
    ],
  },
  {
    name: 'Deadly',
    from: 600,
    to: 999,
    penalties: [
      { stat: 'STRENGTH', amount: -2 },
      { stat: 'ENDURANCE', amount: -1 },
      { stat: 'AGILITY', amount: -2 },
      { stat: 'CURRENT_HIT_POINTS', amount: -5 },
      { stat: 'HEALING_RATE', amount: -5 },
    ],
  },
  {
    name: 'Fatal',
    from: 1000,
    to: null,
    penalties: [
      { stat: 'STRENGTH', amount: -4 },
      { stat: 'PERCEPTION', amount: -3 },
      { stat: 'ENDURANCE', amount: -3 },
      { stat: 'CHARISMA', amount: -3 },
      { stat: 'INTELLIGENCE', amount: -1 },
      { stat: 'AGILITY', amount: -5 },
      { stat: 'CURRENT_HIT_POINTS', amount: -15 },
      { stat: 'HEALING_RATE', amount: -10 },
    ],
  },
];

/** Which band a stored radiation count falls in. Never null. */
export function radiationLevelFor(radiation: number): (typeof RADIATION_LEVELS)[number] {
  for (let i = RADIATION_LEVELS.length - 1; i >= 0; i--) {
    if (radiation >= RADIATION_LEVELS[i]!.from) return RADIATION_LEVELS[i]!;
  }
  return RADIATION_LEVELS[0]!;
}

/** engine/src/interface.h `RADATION_INDICATOR_THRESHOLD` (the misspelling is the engine's). */
export const RADIATION_INDICATOR_THRESHOLD = 65;

/** engine/src/critter.cc `gRadiationEnduranceModifiers` — the save-vs-escalation roll. */
export const RADIATION_ENDURANCE_MODIFIERS: readonly number[] = [2, 0, -2, -4, -6, -8];

// ------------------------------------------------------------------- poison

/** engine/src/critter.cc `poisonEventProcess`: one hit point per tick. */
export const POISON_DAMAGE_PER_TICK = 1;
/** …and the poison count itself drops by two per tick (`critterAdjustPoison(obj, -2)`). */
export const POISON_DECAY_PER_TICK = 2;

/**
 * Ticks between poison hits (`critterAdjustPoison`, critter.cc:362):
 * `10 * (505 - 5 * poison)` game ticks, and there is only ever one pending
 * poison event — every adjustment clears and reschedules.
 *
 * At 36,000 ticks to the in-game hour. Past 100 the expression goes to zero and
 * then negative with no guard in the engine; the editor says so rather than
 * pretending the curve continues.
 */
export function poisonTicksToNext(poison: number): number {
  return 10 * (505 - 5 * poison);
}

/** engine/src/scripts.h `GAME_TIME_TICKS_PER_HOUR`. */
export const GAME_TIME_TICKS_PER_HOUR = 36000;

/**
 * Roughly how long a poison count takes to burn off, in in-game hours, by
 * running the engine's own loop rather than closing the form: poison drops 2 per
 * tick and the interval depends on the *new* value, so it is not a product.
 *
 * Returns null past the point where the engine's interval formula stops being
 * positive, because past there the answer is "the engine does something
 * undefined", not a number.
 */
export function poisonHoursToClear(poison: number): number | null {
  if (poison <= 0) return 0;
  if (poison > 100) return null;
  let remaining = poison;
  let ticks = 0;
  let guard = 0;
  while (remaining > 0 && guard++ < 1000) {
    ticks += poisonTicksToNext(remaining);
    remaining -= POISON_DECAY_PER_TICK;
  }
  return remaining > 0 ? null : ticks / GAME_TIME_TICKS_PER_HOUR;
}

// ------------------------------------------------------- drugs / addiction

export const EVENT_TYPE_DRUG = 0;
export const EVENT_TYPE_WITHDRAWAL = 2;
export const EVENT_TYPE_POISON = 5;
export const EVENT_TYPE_RADIATION = 6;

export const GAME_TIME_TICKS_PER_MINUTE = 600;
export const WITHDRAWAL_DURATION_MINUTES = 10_080;

export type AddictionKey =
  | 'nuka-cola'
  | 'buffout'
  | 'mentats'
  | 'psycho'
  | 'radaway'
  | 'alcohol'
  | 'jet'
  | 'tragic';

export type AddictionRule = {
  key: AddictionKey;
  name: string;
  /** Standard item pid. Alcohol has two pids; the first is used as its queue identity. */
  pid: number;
  alternatePids: readonly number[];
  gvarName: string;
  /** Verified stock index, used only when this install has no name table. */
  stockGvar: number;
  perk: number | null;
  /** Minutes after a dose. Null means the stock item does not use generic withdrawal. */
  onsetMinutes: number | null;
  baseChance: number;
  withdrawal: readonly { stat: string; amount: number }[];
  permanent: boolean;
};

/**
 * item.cc gDrugDescriptions plus the stock drug protos and addiction perk rows.
 * Installed .PRO values can override chance/onset/perk in the UI; the registry
 * identity and GVAR mapping are hard-coded by the engine and cannot be modded by
 * replacing a proto.
 */
export const ADDICTIONS: readonly AddictionRule[] = [
  {
    key: 'nuka-cola', name: 'Nuka-Cola', pid: 106, alternatePids: [],
    gvarName: 'GVAR_NUKA_COLA_ADDICT', stockGvar: 21, perk: 53,
    onsetMinutes: 30, baseChance: 10, withdrawal: [], permanent: false,
  },
  {
    key: 'buffout', name: 'Buffout', pid: 87, alternatePids: [],
    gvarName: 'GVAR_BUFF_OUT_ADDICT', stockGvar: 22, perk: 54,
    onsetMinutes: 2_880, baseChance: 25,
    withdrawal: [
      { stat: 'STRENGTH', amount: -2 },
      { stat: 'ENDURANCE', amount: -2 },
      { stat: 'AGILITY', amount: -3 },
    ],
    permanent: false,
  },
  {
    key: 'mentats', name: 'Mentats', pid: 53, alternatePids: [],
    gvarName: 'GVAR_MENTATS_ADDICT', stockGvar: 23, perk: 55,
    onsetMinutes: 10_080, baseChance: 15,
    withdrawal: [
      { stat: 'INTELLIGENCE', amount: -3 },
      { stat: 'AGILITY', amount: -2 },
    ],
    permanent: false,
  },
  {
    key: 'psycho', name: 'Psycho', pid: 110, alternatePids: [],
    gvarName: 'GVAR_PSYCHO_ADDICT', stockGvar: 24, perk: 56,
    onsetMinutes: 10_080, baseChance: 20,
    withdrawal: [{ stat: 'INTELLIGENCE', amount: -2 }], permanent: false,
  },
  {
    key: 'radaway', name: 'RadAway', pid: 48, alternatePids: [],
    gvarName: 'GVAR_RADAWAY_ADDICT', stockGvar: 25, perk: 57,
    onsetMinutes: 14_400, baseChance: 10,
    withdrawal: [{ stat: 'RADIATION_RESISTANCE', amount: -20 }], permanent: false,
  },
  {
    key: 'alcohol', name: 'Alcohol', pid: 124, alternatePids: [125],
    gvarName: 'GVAR_ALCOHOL_ADDICT', stockGvar: 26, perk: null,
    onsetMinutes: null, baseChance: 0, withdrawal: [], permanent: false,
  },
  {
    key: 'jet', name: 'Jet', pid: 259, alternatePids: [],
    gvarName: 'GVAR_ADDICT_JET', stockGvar: 296, perk: 70,
    onsetMinutes: 2_880, baseChance: 50,
    withdrawal: [
      { stat: 'MAXIMUM_ACTION_POINTS', amount: -1 },
      { stat: 'STRENGTH', amount: -1 },
      { stat: 'PERCEPTION', amount: -1 },
    ],
    permanent: true,
  },
  {
    key: 'tragic', name: 'Tragic cards', pid: 304, alternatePids: [],
    gvarName: 'GVAR_ADDICT_TRAGIC', stockGvar: 295, perk: 71,
    onsetMinutes: null, baseChance: 0,
    withdrawal: [
      { stat: 'PERCEPTION', amount: -2 },
      { stat: 'INTELLIGENCE', amount: -1 },
      { stat: 'LUCK', amount: -1 },
    ],
    permanent: false,
  },
];

export function addictionChance(
  base: number,
  opts: { chemReliant: boolean; chemResistant: boolean; flowerChild: boolean },
): number {
  let chance = base;
  if (opts.chemReliant) chance *= 2;
  if (opts.chemResistant) chance = Math.trunc(chance / 2);
  if (opts.flowerChild) chance = Math.trunc(chance / 2);
  return chance;
}

export function withdrawalDurationMinutes(opts: {
  chemReliant: boolean;
  flowerChild: boolean;
}): number {
  let duration = WITHDRAWAL_DURATION_MINUTES;
  if (opts.chemReliant) duration = Math.trunc(duration / 2);
  if (opts.flowerChild) duration = Math.trunc(duration / 2);
  return duration;
}

// -------------------------------------------------------- honest limitations

/**
 * Things the engine source establishes are *variable*, that a save file does not
 * record, and that the editor therefore assumes the default for.
 *
 * This is displayed, not just documented. An editor that quietly assumed Normal
 * difficulty and vanilla skill costs would be right most of the time and wrong
 * without warning the rest, which is the failure mode this whole panel exists to
 * avoid.
 */
export type Unestablished = { what: string; assumed: string; source: string };

/**
 * The Fallout 1 caveats — the *whole* list for a Fallout 1 save, not additions
 * to the Fallout 2 one.
 *
 * This used to concatenate, on the theory that "the config-override caveats
 * (skill costs, XP table, perk cadence) apply to both engines". They do not.
 * Every entry in `UNESTABLISHED` names a knob that exists only in the Fallout 2
 * fork: `skillsLoadCustomCosts` and `Skills/BasedOnPoints`, sfall's
 * `skillPointsPerLevelModifier` and `set_skill_max`, `pcExperienceTableInit`'s
 * `xp_table`, `perkFrequencyOverride` and `settings.gameplay.perk_carryover`.
 * A grep of the whole of `fallout1-ce/src` finds none of them, so on a Fallout 1
 * save every one of those lines was telling the player a knob might be turned
 * that cannot exist — and the perk-carry-over entry appeared twice with two
 * opposite answers, while the skill-cap entry claimed 300 against Fallout 1's
 * 200. So `unestablishedFor` now replaces rather than concatenates.
 */
export const UNESTABLISHED_FO1: readonly Unestablished[] = [
  {
    what: 'Night Person',
    assumed: 'no time-of-day modifier applied',
    source:
      'trait.cc trait_adjust_stat shifts Perception and Intelligence by ±1 depending on '
      + 'game_time_hour(). The save carries a game time, but the character screen resolves the '
      + 'modifier when it opens, so the stored numbers are shown as stored and the shift is not '
      + 'folded in here.',
  },
  {
    what: 'Ghost',
    assumed: 'no Sneak bonus applied',
    source:
      'perk.cc perk_adjust_skill grants +20 Sneak only while obj_get_visible_light(obj_dude) is at '
      + 'or below 45875 — map lighting at the moment of the read, which a save file does not carry.',
  },
  {
    what: 'Perk carry-over',
    assumed: 'not applicable',
    source:
      'editor.cc UpdateLevel assigns free_perk = 1 rather than incrementing, and Fallout 1 has no '
      + 'carry-over setting. Crossing several perk levels with the character screen closed owes one '
      + 'pick, not several.',
  },
];

/** The caveats that apply to a save from `game`. */
export function unestablishedFor(game: Game = 'fo2'): readonly Unestablished[] {
  return game === 'fo1' ? UNESTABLISHED_FO1 : UNESTABLISHED;
}

export const UNESTABLISHED: ReadonlyArray<Unestablished> = [
  {
    what: 'Skill point cost table',
    assumed: 'the vanilla bands',
    source:
      'skill.cc skillsLoadCustomCosts lets a SkillCost<n> config key replace the whole curve, and '
      + 'Skills/BasedOnPoints switches the lookup from displayed percent to raw points. Neither is '
      + 'recorded in a save.',
  },
  {
    what: 'Skill points per level modifier',
    assumed: '0',
    source:
      'character_editor.cc skillPointsPerLevelModifier is a script-set offset, reset to 0 by '
      + 'characterEditorReset and not stored in a save.',
  },
  {
    what: 'XP thresholds',
    assumed: 'the built-in 500 × L × (L−1) curve',
    source:
      'stat.cc pcExperienceTableInit reads an xp_table key from the content config and replaces the '
      + 'whole curve when it is present. The editor cannot see that config from a save file.',
  },
  {
    what: 'Perk cadence override',
    assumed: 'off',
    source:
      'character_editor.cc perkFrequencyOverride can force any cadence, or disable perks entirely. '
      + 'Script-set, not saved.',
  },
  {
    what: 'Perk carry-over',
    assumed: 'perks accumulate',
    source:
      'character_editor.cc characterEditorGrantPerkAtLevel branches on '
      + 'settings.gameplay.perk_carryover: ON and SFALL increment the owed count, OFF assigns 1 and '
      + 'discards any backlog. The setting is not stored in a save, so raising a level here adds one '
      + 'owed pick per perk level crossed.',
  },
  {
    what: 'Skill display cap',
    assumed: '300',
    source:
      "skill.cc skillMaximum starts at 300 and sfall's set_skill_max can lower it (never raise it). "
      + 'Not recorded in a save.',
  },
];

// ------------------------------------------------------- convenient lookups

export const PERK_EDUCATED = perkIndex('EDUCATED');
export const PERK_TAG = perkIndex('TAG');
export const TRAIT_SKILLED = traitIndex('SKILLED');
export const TRAIT_GIFTED = traitIndex('GIFTED');
export const STAT_INTELLIGENCE = statIndex('INTELLIGENCE');

/** Guards for callers that index tables by skill. */
export const isSkillIndex = (n: number): boolean => Number.isInteger(n) && n >= 0 && n < SKILL_COUNT;
